import { ref } from 'vue';
import axios from '../plugins/axios';

export function useCourses() {
  console.log('useCourses composable initialized');
  const courses = ref([]);
  const loading = ref(false);
  const error = ref(null);
  const statistics = ref(null);
  const instructors = ref([]);
  const categories = ref([]);
  const currentPage = ref(1);
  const totalPages = ref(1);
  const perPage = ref(12);
  const total = ref(0);

  async function fetchCourses(page = 1) {
    try {
      loading.value = true;
      error.value = null;
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }

      console.log('Fetching courses from API... (useCourses)');
      const response = await axios.get(`/admin/courses2`, {
        params: { page, per_page: perPage.value },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      
      // Handle different response structures
      let coursesData = [];
      if (response.data && response.data.success && response.data.data && response.data.data.data) {
        // API returns: { success: true, data: { data: [...courses] } }
        coursesData = response.data.data.data;
      } else if (response.data && response.data.success && response.data.data) {
        // API returns: { success: true, data: [...courses] }
        coursesData = response.data.data;
      } else if (response.data && Array.isArray(response.data)) {
        // API returns: [...courses]
        coursesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        // API returns: { data: [...courses] }
        coursesData = response.data.data;
      } else if (response.data && response.data.courses && Array.isArray(response.data.courses)) {
        // API returns: { courses: [...courses] }
        coursesData = response.data.courses;
      } else {
        console.warn('Unexpected response structure:', response.data);
        coursesData = [];
      }
      
      console.log('Courses data extracted:', coursesData);
      const { data } = { data: coursesData };
      
      // Ensure consistent data types for filtering
      courses.value = Array.isArray(data) ? data.map(course => {
        let instructorObj = course.instructor || {};
        const instructorId = course.instructor_id ? String(course.instructor_id).trim() : '';
        
        return {
          ...course,
          instructor_id: instructorId,
          category_id: course.category_id ? String(course.category_id).trim() : '',
          instructor: {
            ...instructorObj,
            id: instructorObj.id ? String(instructorObj.id).trim() : '',
            first_name: instructorObj.first_name || '',
            last_name: instructorObj.last_name || ''
          }
        };
      }) : [];
      
      console.log('Courses loaded:', courses.value.length);
      
      // Handle pagination metadata
      if (response.data && response.data.data && response.data.data.current_page) {
        // Pagination is in response.data.data
        currentPage.value = response.data.data.current_page || 1;
        totalPages.value = response.data.data.last_page || 1;
        perPage.value = response.data.data.per_page || 12;
        total.value = response.data.data.total || 0;
      } else if (response.data && response.data.meta) {
        // Fallback: pagination is in response.data.meta
        currentPage.value = response.data.meta.current_page || 1;
        totalPages.value = response.data.meta.last_page || 1;
        perPage.value = response.data.meta.per_page || 12;
        total.value = response.data.meta.total || 0;
      }
    } catch (error) {
      error.value = error.message || 'Error fetching courses';
      console.error('Error fetching courses:', error);
      courses.value = [];
    } finally {
      loading.value = false;
    }
  }

  async function fetchStatistics() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/admin/courses/statistics/overview', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      statistics.value = response.data.data;
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  }

  async function fetchInstructors() {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const response = await axios.get('/admin/instructors1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.data.success) {
        instructors.value = response.data.data
          .filter(instructor => instructor !== null && instructor !== undefined)
          .map(instructor => ({
            ...instructor,
            id: instructor.id ? String(instructor.id).trim() : '',
            first_name: instructor.first_name || '',
            last_name: instructor.last_name || ''
          }));
      } else {
        console.error('Failed to fetch instructors:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
    }
  }

  async function fetchCategories() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/admin/coursecategories2', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.data.success) {
        categories.value = response.data.data;
      } else {
        console.error('Failed to fetch categories:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async function deleteCourse(id) {
    try {
      // API call to delete course
      // Example: await api.delete(`/courses/${id}`)
      
      // For demo: Update local state
      courses.value = courses.value.filter(course => course.id !== id);
      return true;
    } catch (error) {
      console.error('Error deleting course:', error);
      error.value = error.message;
      return false;
    }
  }
  
  async function updateCoursesInBulk(courseIds, updates) {
    try {
      // Get the authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      // For a real API implementation, you would use:
      // const response = await axios.post('https://api.kewaar.com/api/admin/courses/bulk-update', 
      //   { ids: courseIds, updates },
      //   { 
      //     headers: {
      //       'Authorization': `Bearer ${token}`,
      //       'Accept': 'application/json',
      //       'Content-Type': 'application/json'
      //     }
      //   }
      // );
      
      // For now, we'll update the local state for the demo
      courses.value = courses.value.map(course => {
        if (courseIds.includes(course.id)) {
          return { ...course, ...updates };
        }
        return course;
      });
      
      console.log(`Bulk updated ${courseIds.length} courses with:`, updates);
      return true;
    } catch (error) {
      console.error('Error updating courses in bulk:', error);
      error.value = error.message;
      return false;
    }
  }

  return {
    courses,
    loading,
    error,
    statistics,
    instructors,
    categories,
    currentPage,
    totalPages,
    perPage,
    total,
    fetchCourses,
    fetchStatistics,
    fetchInstructors,
    fetchCategories,
    deleteCourse,
    updateCoursesInBulk
  };
} 